import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Shield,
  ShieldAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import {
  useAuditLog,
  useAuditVerify,
  type AuditEntry,
  type AuditFilter,
  type AuditVerifyResult,
} from '@/hooks/useAuditLog';
import { formatDateTime } from '@/lib/format';

/**
 * /settings/audit - Audit-Log-Viewer (Sprint 5, WT-508).
 *
 * Append-only Audit-Trail aus Postgres. Hash-Chain-Verify-Button rechnet die
 * Kette gegen jeden Eintrag und meldet Tampering. Diff-Spalte ist als
 * Collapse rendered (JSON kann lang werden).
 */

export const Route = createFileRoute('/settings/audit')({
  component: AuditPage,
});

function AuditPage(): JSX.Element {
  const [filter, setFilter] = useState<AuditFilter>({});
  const auditQuery = useAuditLog(filter);
  const verify = useAuditVerify();

  const setKey = <K extends keyof AuditFilter>(key: K, value: AuditFilter[K]): void => {
    const next = { ...filter };
    if (value === undefined || value === '' || value === null) {
      delete next[key];
    } else {
      next[key] = value;
    }
    setFilter(next);
  };

  const entries: AuditEntry[] = auditQuery.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="size-5 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-semibold">Audit-Log</h1>
          </div>
          <p className="text-muted">
            Append-only Audit-Trail. Hash-Chain-Verify zeigt, ob Tampering stattgefunden hat.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            onClick={() => void auditQuery.refetch()}
            loading={auditQuery.isFetching}
          >
            Aktualisieren
          </Button>
          <Button
            leftIcon={<ShieldAlert className="size-4" />}
            onClick={() => verify.mutate()}
            loading={verify.isPending}
            data-testid="audit-verify"
          >
            Hash-Chain prüfen
          </Button>
        </div>
      </header>

      {verify.data && <VerifyResultBanner result={verify.data} />}
      {verify.isError && <ErrorAlert error={verify.error} />}

      <Card>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Actor</span>
            <input
              type="text"
              value={filter.actor ?? ''}
              onChange={(e) => setKey('actor', e.target.value || undefined)}
              placeholder="z.B. admin"
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              data-testid="audit-filter-actor"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Action</span>
            <input
              type="text"
              value={filter.action ?? ''}
              onChange={(e) => setKey('action', e.target.value || undefined)}
              placeholder="z.B. run.start"
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              data-testid="audit-filter-action"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Ab</span>
            <input
              type="datetime-local"
              value={filter.since ?? ''}
              onChange={(e) => setKey('since', e.target.value || undefined)}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Limit</span>
            <input
              type="number"
              min={10}
              max={500}
              value={filter.limit ?? 100}
              onChange={(e) =>
                setKey('limit', Number(e.target.value) || 100)
              }
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
            />
          </label>
        </div>
      </Card>

      <Card>
        {auditQuery.isLoading && <SkeletonRows rows={6} cols={5} />}
        {auditQuery.isError && <ErrorAlert error={auditQuery.error} />}
        {!auditQuery.isLoading && entries.length === 0 && (
          <EmptyState
            icon={<Shield className="size-8" />}
            title="Keine Audit-Einträge"
            description="Ggf. Filter zu eng oder noch keine Aktionen ausgeführt."
          />
        )}
        {entries.length > 0 && (
          <DataTable
            caption="Audit-Log"
            columns={AUDIT_COLUMNS}
            rows={entries}
            rowKey={(e) => String(e.id)}
            storageKey="audit-log"
            defaultPageSize={100}
          />
        )}
      </Card>
    </div>
  );
}

const AUDIT_COLUMNS: ReadonlyArray<Column<AuditEntry>> = [
  {
    id: 'zeit',
    header: 'Zeit',
    className: 'whitespace-nowrap',
    filterValue: (e) => {
      const label = formatDateTime(e.occurred_at);
      return label === '–' ? '' : label;
    },
    cell: (e) => (
      <RelativeTime value={e.occurred_at} variant="absolute-with-tooltip" />
    ),
  },
  {
    id: 'actor',
    header: 'Actor',
    filter: 'select',
    className: 'text-xs',
    cell: (e) => e.actor,
  },
  {
    id: 'action',
    header: 'Action',
    filter: 'select',
    filterValue: (e) => e.action,
    cell: (e) => <code className="font-mono text-xs">{e.action}</code>,
  },
  {
    id: 'subject',
    header: 'Subject',
    filterValue: (e) => [e.entity_type, e.entity_id ?? ''].join(' '),
    cell: (e) => (
      <span className="text-xs">
        <Badge tone="neutral">{e.entity_type}</Badge>
        {e.entity_id && (
          <code className="ml-1 font-mono text-xs">
            {e.entity_id.slice(0, 8)}
          </code>
        )}
      </span>
    ),
  },
  {
    id: 'summary',
    header: 'Summary',
    className: 'max-w-md text-xs text-muted',
    filterValue: (e) => e.summary ?? '',
    cell: (e) => e.summary ?? '–',
  },
  {
    id: 'diff',
    header: 'Diff',
    filter: false,
    cell: (e) => <AuditDiffCell entry={e} />,
  },
];

function AuditDiffCell({ entry }: { entry: AuditEntry }): JSX.Element {
  const [open, setOpen] = useState(false);
  const diff = entry.diff;
  if (diff == null || Object.keys(diff).length === 0) {
    return <span className="text-xs text-muted">–</span>;
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md px-1 text-xs text-primary hover:underline"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {open ? 'Verbergen' : 'Anzeigen'}
      </button>
      {open && (
        <pre className="mt-1 max-h-48 overflow-auto rounded-md border border-border bg-surface-2 p-2 font-mono text-[11px]">
          {JSON.stringify(diff, null, 2)}
        </pre>
      )}
    </div>
  );
}

function VerifyResultBanner({ result }: { result: AuditVerifyResult }): JSX.Element {
  if (result.valid) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success"
        data-testid="audit-verify-ok"
      >
        <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          Hash-Chain ist intakt. {result.entries_checked} Einträge validiert.
        </span>
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger"
      data-testid="audit-verify-broken"
    >
      <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>
        Hash-Chain GEBROCHEN
        {result.broken_at && (
          <>
            {' '}an Entry <code className="font-mono">#{result.broken_at}</code>
          </>
        )}
        {result.broken_reason && (
          <>
            {' '}({result.broken_reason})
          </>
        )}
        . Tampering möglich - Backup prüfen.
      </span>
    </div>
  );
}
